/** Change log drawer + presence + sync status — replaces the old version
 * chips / fork detection with a server-backed history trail. */
import { STATUS_LABELS } from "@trip/shared/engine/checklist";
import { esc, fmin } from "@trip/shared/engine/time";
import type { HistoryEntry } from "@trip/shared/protocol";
import { isFixed, type SlotValue } from "@trip/shared/types";
import { store } from "../store.js";
import { banner, clearBanner } from "./banner.js";

function fmtWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
}

/** "Sep 20 · Long lamb lunch → Konoba Kopačina · 🔒 Booked" */
export function describeChange(h: HistoryEntry): string {
  const def = store.def;
  if (h.kind === "import") return h.key;
  if (h.kind === "slot") {
    // extra slots are keyed d13_x1 — the old /(\d+)$/ tail missed the "x",
    // which sent day/item lookups down the null branch and printed "?"
    const m = h.key.match(/^(d\d+)_(x?\d+)$/);
    const day = m && def.days.find((d) => d.id === m[1]);
    const isExtra = m ? m[2].startsWith("x") : false;
    const item = day && !isExtra ? day.items[Number(m![2])] : undefined;
    const v = h.newValue as SlotValue | null;
    const slotLabel = item && !isFixed(item) ? item.s : isExtra ? "✚ Extra time" : h.key;
    const actName = v && def.act[v.act] ? def.act[v.act].name.split("—")[0].trim() : v?.act ?? "?";
    return `${day ? day.date : "?"} · ${slotLabel} → ${actName}${v ? ` · ${STATUS_LABELS[v.st]}` : ""}${v && v.dur === 0 ? " (skipped)" : ""}`;
  }
  if (h.kind === "dayend") {
    const day = def.days.find((d) => d.id === h.key);
    const min = (h.newValue as { min: number } | null)?.min;
    return `${day ? day.date : "?"} · 🏁 back-at-base target${min !== undefined ? ` → ${fmin(min)}` : ""}`;
  }
  if (h.kind === "fixdur") {
    const m = h.key.match(/^(d\d+)_(\d+)$/);
    const day = m && def.days.find((d) => d.id === m[1]);
    const item = day && day.items[Number(m![2])];
    const label = item && isFixed(item) ? item.l.split("·")[0].trim() : h.key;
    const min = (h.newValue as { min: number } | null)?.min;
    return `${day ? day.date : "?"} · ${label} → ${min && min > 0 ? `${min} min` : "auto"}`;
  }
  const on = (h.newValue as { on: boolean } | null)?.on;
  const bl = def.bl.find((b) => b.k === h.key);
  const what = bl ? bl.what : h.key.startsWith("auto_") && def.act[h.key.slice(5)] ? def.act[h.key.slice(5)].name : h.key;
  return `checklist: ${what.split("—")[0].trim()} ${on ? "✓ done" : "· unticked"}`;
}

let open = false;

export async function toggleChangelog() {
  const el = document.getElementById("changelog")!;
  if (open) {
    el.style.display = "none";
    open = false;
    return;
  }
  el.style.display = "";
  el.innerHTML = `<div class="cl-loading">Loading history…</div>`;
  open = true;
  try {
    const res = await fetch(`/api/trip/${encodeURIComponent(store.tripId)}/history?limit=200`, {
      headers: store.key ? { "x-trip-key": store.key } : {},
    });
    const data = (await res.json()) as { entries: HistoryEntry[] };
    el.innerHTML = data.entries.length
      ? `<div class="cl-title">Change log — newest first</div>` +
        data.entries
          .map(
            (h) =>
              `<div class="cl-row"><span class="cl-who">${esc(h.editor)}</span><span class="cl-what">${esc(describeChange(h))}</span><span class="cl-when">${fmtWhen(h.at)}</span></div>`
          )
          .join("")
      : `<div class="cl-loading">No changes yet.</div>`;
  } catch {
    el.innerHTML = `<div class="cl-loading">Couldn't load history — are you online?</div>`;
  }
}

/**
 * Only OFFLINE surfaces a banner — being online is the normal state and
 * announcing it was noise.
 *
 * Connection is ASSUMED until proven otherwise. `store.online` starts false
 * and only flips true when the socket's "welcome" lands, and onStatus fires
 * once on subscribe — so treating that first false as "offline" showed the
 * warning on every page load, during the second before the socket connected.
 * A short grace period is what separates "still connecting" from "actually
 * offline": the banner only appears if we're still down after it, and any
 * reconnection inside it cancels the warning before it is ever seen.
 */
const OFFLINE_GRACE_MS = 4000;
let offlineTimer: ReturnType<typeof setTimeout> | null = null;
let bannerShown = false;

export function renderStatus(online: boolean, _presence: string[]) {
  if (online) {
    if (offlineTimer) {
      clearTimeout(offlineTimer);
      offlineTimer = null;
    }
    if (bannerShown) {
      bannerShown = false;
      clearBanner();
    }
    return;
  }
  // offline: arm the grace period once, and let an existing one keep running
  if (offlineTimer || bannerShown) return;
  offlineTimer = setTimeout(() => {
    offlineTimer = null;
    bannerShown = true;
    banner("○ Offline — the itinerary is read-only until you're reconnected.", "warn");
  }, OFFLINE_GRACE_MS);
}
